import { ReactElement } from 'react';
import Banner from './Banner';
import { useUser } from '../../../contexts/UserContext';
import { SubscriptionStatus } from '../../../models/user.types';
import { useMenuWithCart } from '../../../contexts/MenuWithCart';
import { useNotification } from '../../../contexts/NotificationContext';
import { Toast, ToastVariant } from '../notification/Toast';
import { useUpdateCartSkipMutation } from '../../../services/cart/cart.queries';
import { BannerVariant } from '../../../utils/config';

function ReactivationBannerContainer(): ReactElement {
  const { user } = useUser();
  const {
    cart,
    reactivateSubscription,
  } = useMenuWithCart();
  const { showNotification } = useNotification();
  const updateCartMutation = useUpdateCartSkipMutation();

  if (!user?.status) {
    return <></>;
  }
  const {
    status,
    firstName,
  } = user;

  let bannerText;
  let bannerBoldText;
  let buttonText;
  let isVisible;
  let onClick;
  let heapId;

  const handleReactivation = async (): Promise<void> => {
    await reactivateSubscription();
    showNotification({
      content: <Toast variant={ToastVariant.ACCOUNT_ACTIVE} />,
      duration: 5000,
    });
  };

  const handleSuspendedUser = (): void => {
    window.location.href = 'payment-method';
  };

  const handleUnskip = (): void => {
    if (cart) {
      updateCartMutation.mutate({
        cartId: cart.id,
        isSkipped: cart.isSkipped,
      });
    }
    showNotification({
      content: <Toast variant={ToastVariant.CART_UNSKIPPED} />,
      duration: 5000,
    });
  };

  // determine banner text and visibility
  switch (status) {
    case SubscriptionStatus.CANCELLED:
      bannerText = ' Your account is cancelled.';
      bannerBoldText = `Nice to see you, ${firstName}!`;
      buttonText = 'Reactivate Subscription';
      isVisible = true;
      onClick = handleReactivation;
      heapId = 'banner_reactivate_button';
      break;
    case SubscriptionStatus.SUSPENDED:
      bannerText = '';
      bannerBoldText = 'Welcome back! To resume orders, add a new payment to your account.';
      buttonText = 'Update Payment';
      isVisible = true;
      onClick = handleSuspendedUser;
      heapId = 'banner_update_payment_button';
      break;
    case SubscriptionStatus.PAUSED:
    case SubscriptionStatus.RETURNING:
      bannerText = ' Your account is paused.';
      bannerBoldText = 'Nice to see you!';
      buttonText = 'Resume Orders';
      isVisible = true;
      onClick = handleReactivation;
      heapId = 'banner_resume_orders_button';
      break;
    default:
      isVisible = false;
  }

  if (!isVisible && cart?.isSkipped) {
    bannerText = ' ';
    bannerBoldText = 'Your order is skipped.';
    buttonText = 'Unskip Order';
    isVisible = true;
    onClick = handleUnskip;
    heapId = 'banner_unskip_button';
  }

  return (
    <Banner
      bannerText={bannerText}
      bannerBoldText={bannerBoldText}
      buttonText={buttonText}
      onClick={onClick}
      isVisible={isVisible}
      variant={BannerVariant.REACTIVATION}
      heapId={heapId}
    />
  );
}

export default ReactivationBannerContainer;
